import { X } from 'lucide-react';
import { type ComponentProps, type ReactNode } from 'react';

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';

export function SelectionBar({
    selectedCount,
    onClearSelection,
    children,
    className,
    ...props
}: ComponentProps<'div'> & {
    readonly selectedCount: number;
    readonly onClearSelection: () => void;
}) {
    return (
        <div
            {...props}
            role="toolbar"
            className={cn(
                'flex -translate-x-1/2 items-center gap-2 rounded-full bg-zinc-900 px-3.5 py-1.5 text-zinc-100 shadow-[0_2px_8px_rgb(0_0_0_/_0.18)]',
                className,
            )}
        >
            <span
                aria-live="polite"
                className="text-sm font-medium whitespace-nowrap text-zinc-200"
            >
                {selectedCount} selected
            </span>
            <div className="flex items-center gap-0.5">{children}</div>
            <button
                type="button"
                aria-label="Clear selection"
                onClick={onClearSelection}
                className="flex size-7 items-center justify-center rounded-md text-zinc-400 hover:bg-white/10 hover:text-zinc-100"
            >
                <X className="size-4" />
            </button>
        </div>
    );
}

export function SelectionBarAction({
    label,
    tooltip = label,
    children,
    className,
    ...props
}: ComponentProps<'button'> & {
    readonly label: string;
    readonly tooltip?: ReactNode;
}) {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <button
                    {...props}
                    type="button"
                    aria-label={label}
                    className={cn(
                        'flex size-7 items-center justify-center rounded-md hover:bg-white/10 disabled:opacity-50',
                        className,
                    )}
                >
                    {children}
                </button>
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
    );
}
